import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Briefcase,
  TrendingUp,
  LogOut,
  Lock,
  CheckCircle,
  XCircle,
  Globe,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";

function ProfileView({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({ total_sales: 0, total_revenue: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("app_language", lang);
  };

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordStatus, setPasswordStatus] = useState({
    type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTodayStats = async () => {
      try {
        setLoadingStats(true);
        // Get today's date range
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const response = await api.get(
          `/sales/stats?startDate=${today.toISOString()}&endDate=${tomorrow.toISOString()}`,
        );
        if (response.data.success) {
          setStats(response.data.stats);
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchTodayStats();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordStatus({ type: "", message: "" });

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordStatus({
        type: "error",
        message: "New passwords do not match",
      });
      return;
    }

    if (passwordForm.new_password.length < 6) {
      setPasswordStatus({
        type: "error",
        message: "Password must be at least 6 characters",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await api.post("/auth/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });

      if (response.data.success) {
        setPasswordStatus({
          type: "success",
          message: "Password changed successfully",
        });
        setPasswordForm({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setTimeout(() => {
          setIsChangingPassword(false);
          setPasswordStatus({ type: "", message: "" });
        }, 2000);
      }
    } catch (error) {
      setPasswordStatus({
        type: "error",
        message: error.response?.data?.error || "Failed to change password",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
    }).format(amount);
  };

  return (
    <div className="profile-view">
      <div className="profile-card">
        <div className="profile-avatar">
          <User size={48} />
        </div>
        <h2 className="profile-name">
          {user?.full_name || user?.name || "Promoter"}
        </h2>
        <p className="profile-role">
          {user?.role === "promoter"
            ? t("se.profile.salesPromoter")
            : t("se.profile.administrator")}
        </p>
      </div>

      <div className="profile-info-card">
        <h3 className="profile-section-title">
          {t("se.profile.todayPerformance")}
        </h3>
        <div className="profile-stats-grid">
          <div className="profile-stat-box">
            <Briefcase size={24} className="stat-icon" />
            <div className="stat-details">
              <span className="stat-label">{t("se.profile.salesCount")}</span>
              <span className="stat-value">
                {loadingStats ? "..." : stats.total_sales}
              </span>
            </div>
          </div>
          <div className="profile-stat-box">
            <TrendingUp size={24} className="stat-icon" />
            <div className="stat-details">
              <span className="stat-label">{t("se.profile.totalRevenue")}</span>
              <span className="stat-value">
                {loadingStats ? "..." : formatCurrency(stats.total_revenue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-info-card">
        <h3 className="profile-section-title">
          {t("se.profile.accountDetails")}
        </h3>
        <div className="profile-info-row">
          <Mail size={18} />
          <div>
            <span className="profile-label">Email Address</span>
            <span className="profile-value">{user?.email}</span>
          </div>
        </div>

        {!isChangingPassword ? (
          <button
            className="change-password-btn"
            onClick={() => setIsChangingPassword(true)}
          >
            <Lock size={18} /> {t("se.profile.changePassword")}
          </button>
        ) : (
          <div className="password-change-form">
            <h4 className="password-form-title">
              {t("se.profile.passwordFormTitle")}
            </h4>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>
                  {t("se.profile.currentPassword")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      current_password: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  {t("se.profile.newPassword")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      new_password: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  {t("se.profile.confirmNewPassword")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirm_password: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {passwordStatus.message && (
                <div className={`password-status ${passwordStatus.type}`}>
                  {passwordStatus.type === "success" ? (
                    <CheckCircle size={16} />
                  ) : (
                    <XCircle size={16} />
                  )}
                  {passwordStatus.message}
                </div>
              )}

              <div className="password-form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordStatus({ type: "", message: "" });
                    setPasswordForm({
                      current_password: "",
                      new_password: "",
                      confirm_password: "",
                    });
                  }}
                  disabled={isSubmitting}
                >
                  {t("se.profile.cancel")}
                </button>
                <button
                  type="submit"
                  className="save-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("se.profile.saving")
                    : t("se.profile.savePassword")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="profile-info-card">
        <h3 className="profile-section-title">
          <Globe
            size={18}
            style={{
              display: "inline",
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {t("se.profile.language")}
        </h3>
        <div className="profile-language-options">
          <button
            type="button"
            className={`profile-language-btn${i18n.language === "en" ? " active" : ""}`}
            onClick={() => handleLanguageChange("en")}
          >
            EN
          </button>
          <button
            type="button"
            className={`profile-language-btn${i18n.language === "ms" ? " active" : ""}`}
            onClick={() => handleLanguageChange("ms")}
          >
            BM
          </button>
        </div>
      </div>

      <button onClick={onLogout} className="profile-logout-btn">
        <LogOut size={20} /> {t("se.profile.signOut")}
      </button>
    </div>
  );
}

export default ProfileView;
